import { ExploreSingleInsight } from '@toggle/toggle';
import React, { useEffect, useRef, useState } from 'react';

import { ExploreInsightCard } from '~/components/insights-group/insight-card/ExploreInsightCard';
import { useQueryInsights } from '~/react-query-hooks/insights/useInsight';
import { EmptyInsights } from '~/views/insights/components/insights-drawer/empty-insights/EmptyInsights';
import { InsightsLoadingCard } from '~/views/insights/components/insights-drawer/insights-loading-card/InsightsLoadingCard';
import { IconConfig } from '~/views/insights/components/insights-drawer/InsightsDrawer';

import * as S from './InsightsList.styles';

export interface InsightsListProps {
  selectedInsights: ExploreSingleInsight[];
  activeInsightId: string;
  isInsightsLoading: boolean;
  handleSetActiveInsightId: (insightId: string) => void;
  handleSetIconConfig?: (config: IconConfig) => void;
}

export const InsightsList = ({
  selectedInsights,
  activeInsightId,
  isInsightsLoading,
  handleSetActiveInsightId,
  handleSetIconConfig,
}: InsightsListProps) => {
  const [initialized, setInitialized] = useState(false);
  const cardsContainerRef = useRef<HTMLDivElement | null>(null);
  const insightRefs = useRef<(HTMLDivElement | null)[]>([]);

  const activeElement = Object.values(insightRefs.current).find(
    ref => ref?.dataset.insightId === activeInsightId
  );

  useEffect(() => {
    if (initialized) {
      return;
    }

    if (activeElement && cardsContainerRef.current) {
      const containerTop =
        cardsContainerRef.current.getBoundingClientRect().top;
      const elementTop = activeElement.getBoundingClientRect().top;

      const offset = elementTop - containerTop;

      cardsContainerRef.current.scrollTo({
        top: cardsContainerRef.current.scrollTop + offset,
        behavior: 'instant',
      });
      setInitialized(true);
    }
  }, [activeElement, initialized]);

  const insightsId = selectedInsights.map(insight => insight.id);
  const { data, isFetching, isError } = useQueryInsights(
    insightsId,
    !!insightsId.length
  );
  const insights = data
    ?.slice()
    ?.sort(
      (a, b) =>
        new Date(b.created_at).valueOf() - new Date(a.created_at).valueOf()
    );

  if (isFetching || isInsightsLoading) {
    return <InsightsLoadingCard />;
  }

  if (isError) {
    return <S.StyledBaseErrorView />;
  }

  if (!insights.length) {
    return <EmptyInsights />;
  }

  return (
    <S.Cards ref={cardsContainerRef}>
      {insights?.map((insight, index) => {
        const exploreInsight = selectedInsights.find(s => s.id === insight.id);

        if (!exploreInsight) {
          return null;
        }

        return (
          <ExploreInsightCard
            key={insight.id}
            insight={insight}
            exploreInsight={exploreInsight}
            activeInsightId={activeInsightId}
            handleSetActiveInsightId={handleSetActiveInsightId}
            handleSetIconConfig={handleSetIconConfig}
            ref={(el: HTMLDivElement | null) => {
              insightRefs.current[index] = el;
            }}
            dataInsightId={insight.id}
          />
        );
      })}
    </S.Cards>
  );
};
